import React, { useEffect, useState } from 'react';
import { UnknownAction } from 'redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { css } from '@emotion/core';

import { Box, breakpoints } from '../../../design-system';
import { RootState } from '../../../store/rootReducer';
import Menu from './Menu';
import paths from '../../routing/paths';
import accountPaths from '../paths';
import { FeedbackCallback, Forms, FormPersonal } from '../../form/types';
import { logout } from '../../auth/actions';
import { setFeedback } from '../../form/actions';
import { hasToken } from '../../api';
import { User } from '../../auth/types';
import { ErrorUserModal } from './ErrorUserModal';
import { openModal } from '../../common/actions';
import { heights } from '../../common/constants';
import { cmsUspInit } from '../../cms/actions';
import { UspSection } from '../../home/components/UspSection';
import { SearchMostWanted } from '../../search/components/SearchMostWanted';
import { IMAGE_WIDTH, SearchBannerTiles } from '../../search/components/SearchBannerTiles';

type Props = {
  form: FormPersonal;
  logout: () => void;
  setFeedback: (feedback: FeedbackCallback) => void;
  user?: User;
};

export const Account = ({ logout, setFeedback, form, user }: Props) => {
  const [hasSessionConfigured, setHasSessionConfigured] = useState(false);
  const { user: userAccount } = useSelector((state: RootState) => state.account);
  const { body2: cmsSearchMostWanted, body3: cmsBannerTiles } = useSelector(
    (state: RootState) => state.cms.search
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { usp, usp_main_title } = useSelector((state: RootState) => state.cms.usp) ?? {};

  const shouldShowUsp =
    usp?.length > 0 &&
    usp_main_title &&
    (pathname.startsWith(paths.ACCOUNT + accountPaths.ORDERS_OFFLINE) ||
      pathname.startsWith(paths.ACCOUNT + accountPaths.ORDERS));

  useEffect(() => {
    if (!usp?.length || !usp_main_title) {
      dispatch(cmsUspInit() as unknown as UnknownAction);
    }
  }, [usp, usp_main_title]);

  useEffect(() => {
    setFeedback({
      form: Forms.personal,
      ok: false,
      message: '',
      isDirty: form.feedback.isDirty,
    });
    setFeedback({
      form: Forms.address,
      ok: false,
      message: '',
      isDirty: form.feedback.isDirty,
    });
  }, [pathname]);

  const asyncSession = async () => {
    const isAuthorized = await hasToken();
    const isDeletedAccount = userAccount?.error === 'customer_deleted';
    if (!isAuthorized || isDeletedAccount) {
      dispatch(
        openModal({
          content: <ErrorUserModal isDeletedAccount={isDeletedAccount} isSessionExpiration />,
          preset: 'paymentError',
        })
      );
    } else {
      setHasSessionConfigured(true);
    }
  };

  useEffect(() => {
    asyncSession();
  }, [userAccount]);

  if (!hasSessionConfigured) {
    return null;
  }

  if (pathname === paths.ACCOUNT) {
    navigate(paths.ACCOUNT + accountPaths.INFO);
  }

  return (
    <>
      <Helmet>
        <title>Mon compte | Darjeeling</title>
      </Helmet>
      <Box display="flex" flexDirection={['column', 'row']} mx={['m', 'l']}>
        <Box
          width={[1, 0.3, 0.2]}
          mb="xl"
          css={css`
            @media (min-width: ${breakpoints.S}px) {
              position: sticky;
              height: fit-content;
              top: ${heights.HEADER}px;
            }
          `}
        >
          <Menu user={user} logout={logout} />
        </Box>
        <Box width={[1, 0.7, 0.8]} my={['na', 'xxl']}>
          <Outlet />
        </Box>
      </Box>
      {pathname.startsWith(paths.ACCOUNT + accountPaths.RETURNS) && (
        <div
          css={css`
            display: flex;
            gap: 16px;
            margin: 0 16px;
            flex-direction: column;
            margin-bottom: 32px;

            @media (min-width: ${breakpoints.S}px) {
              margin: 0 48px 32px 48px;
            }

            @media (min-width: ${breakpoints.L}px) {
              flex-direction: row;
            }
          `}
        >
          <SearchMostWanted data={cmsSearchMostWanted?.[0]} />
          <div
            css={css`
              img {
                width: calc(50vw - 24px) !important;
                height: 100%;
                object-fit: cover;
                border-radius: 8px;

                @media (min-width: ${breakpoints.S}px) {
                  width: calc(50vw - 56px) !important;
                }

                @media (min-width: ${breakpoints.L}px) {
                  width: ${IMAGE_WIDTH}px !important;
                }
              }

              .image-wrapper {
                height: 100%;
              }
            `}
          >
            <SearchBannerTiles data={cmsBannerTiles?.[0]} />
          </div>
        </div>
      )}
      {shouldShowUsp && <UspSection content={usp} title={usp_main_title} />}
    </>
  );
};

export default connect(
  (state: RootState) => ({
    form: state.form.personal,
    user: state.auth.user,
  }),
  {
    logout,
    setFeedback,
  }
)(Account);
